import { FileType, FILE_EXTENSION_MAP } from '../types';

/**
 * Extracts file extension from URL (including query params)
 * Examples:
 *   "file.pdf" -> ".pdf"
 *   "file.pdf?version=1" -> ".pdf"
 *   "https://example.com/path/to/file.jpg" -> ".jpg"
 */
export function getFileExtensionFromUrl(url: string): string {
  try {
    // Parse URL to handle query params
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;

    // Extract extension from pathname
    const lastDot = pathname.lastIndexOf('.');
    if (lastDot === -1) return '';

    return pathname.slice(lastDot).toLowerCase();
  } catch {
    // Fallback: treat as simple path
    const lastDot = url.lastIndexOf('.');
    if (lastDot === -1) return '';

    // Extract extension before query params
    const extension = url.slice(lastDot).split('?')[0].split('#')[0];
    return extension.toLowerCase();
  }
}

/**
 * Detects file type from URL
 * @param url - File URL
 * @returns FileType or null if unsupported
 */
export function detectFileType(url: string): FileType | null {
  if (!url) return null;

  const extension = getFileExtensionFromUrl(url);
  if (!extension) return 'unsupported';

  const fileType = FILE_EXTENSION_MAP[extension as keyof typeof FILE_EXTENSION_MAP];
  return fileType || 'unsupported';
}

/**
 * Validates if a URL is HTTP/HTTPS or blob
 */
export function isValidHttpUrl(url: string): boolean {
  if (!url) return false;

  try {
    const urlObj = new URL(url);
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:' || urlObj.protocol === 'blob:';
  } catch {
    return false;
  }
}

/**
 * Checks if file type is supported
 */
export function isFileTypeSupported(url: string): boolean {
  const fileType = detectFileType(url);
  return fileType !== null && fileType !== 'unsupported';
}

/**
 * Gets the viewer type needed for a URL
 */
export function getViewerType(url: string): string {
  const fileType = detectFileType(url);

  if (!fileType || fileType === 'unsupported') {
    return 'unsupported';
  }

  return fileType;
}
